import { AavoMosaic } from "../../../../common/mosaic/AavoMosaic.tsx";
import { useContext, useState } from "react";
import { CustomerOrderDataGridApi_CustomerOrderDto } from "src/api/generated/erp/sales/customerOrder/api/customerOrderDataGridApi.ts";
import { CustomerOrderState } from "src/api/generated/erp/db/types/enums/customerOrderState.ts";
import { CustomerOrdersDataGrid } from "./CustomerOrdersDataGrid.tsx";
import i18n from "i18next";
import { CustomerOrderLinesDataGrid } from "src/components/views/erp/sales/customerOrderLine/CustomerOrderLinesDataGrid.tsx";
import { CenteredTypography } from "../../../../common/CenteredTypography.tsx";
import { SingleSourceTasksView } from "src/components/views/tasks/pages/SingleSourceTasksView.tsx";
import { DeliveryPackagesDataGrid } from "../../delivery/deliveryPackage/DeliveryPackagesDataGrid.tsx";
import { DeliveryPackageLinesDataGrid } from "src/components/views/erp/delivery/deliveryPackage/DeliveryPackageLinesDataGrid.tsx";
import { DeliveryView } from "src/api/generated/erp/db/types/tables/deliveryView.ts";
import { DeliveriesDataGridBase } from "../../delivery/delivery/DeliveriesDataGridBase.tsx";
import { CustomerOrderInvoicesDataGrid } from "src/components/views/erp/sales/invoicing/customerOrderInvoicing/CustomerOrderInvoicesDataGrid.tsx";
import { CustomerOrderAttributesForm } from "src/components/views/erp/sales/customerOrder/CustomerOrderAttributesForm.tsx";
import { CustomerOrderBillingPlanCrudDataGrid } from "src/components/views/erp/sales/billingPlan/customerOrder/CustomerOrderBillingPlanCrudDataGrid.tsx";
import { CustomerOrderInfoAndCommentsView } from "./CustomerOrderInfoAndCommentsView.tsx";
import { CustomerOrderReclamationsDataGrid } from "./CustomerOrderReclamationsDataGrid.tsx";
import { useRefreshRef } from "src/utils/useRefreshRef.ts";
import { CustomerOrdersPageContext } from "./CustomerOrdersPageContext.tsx";
import { CustomerOrderSalesConditionDataGrid } from "./CustomerOrderSalesConditionDataGrid.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { CustomerOrderSalesCommissionView } from "src/components/views/erp/sales/salesCommissions/orderAndOfferViews/order/CustomerOrderSalesCommissionView.tsx";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { useTasksEnabled } from "src/components/views/tasks/useTasksEnabled.ts";
import { DeliveriesDataGridApi } from "src/api/generated/erp/delivery/api/deliveriesDataGridApi.ts";
import { DeliveryPackageWithSourcesView } from "src/api/generated/erp/db/types/tables/deliveryPackageWithSourcesView.ts";
import { CustomerOrderChangesDataGrid } from "src/components/views/erp/sales/customerOrderChange/CustomerOrderChangesDataGrid.tsx";
import { CostEventsOfObjectDataGrid } from "src/components/views/erp/common/costEvents/CostEventsOfObjectDataGrid.tsx";
import { PartCostOverridesDataGrid } from "src/components/views/erp/sales/partCostOverride/PartCostOverridesDataGrid.tsx";
import { CustomerOrderProfitMarginsDataGrid } from "src/components/views/erp/sales/customerOrder/CustomerOrderProfitMarginsDataGrid.tsx";

export interface CustomerOrdersPageProps {
	onlyCustomerOrderToShowId?: number;
	customerIdFilter?: number;
	initialStateFilter?: CustomerOrderState[];
	isActive?: boolean;
}

export const CustomerOrdersPage = ({
	onlyCustomerOrderToShowId,
	customerIdFilter,
	initialStateFilter,
}: CustomerOrdersPageProps) => {
	const [selectedCustomerOrder, setSelectedCustomerOrder] = useState<
		CustomerOrderDataGridApi_CustomerOrderDto | undefined
	>(undefined);

	const selectCustomerOrderTypography = <CenteredTypography>{i18n.t("select_customer_order")}</CenteredTypography>;

	const customerOrdersRefreshRef = useRefreshRef();
	const customerOrderLinesRefreshRef = useRefreshRef();
	const attributeViewRefreshRef = useRefreshRef();
	const tasksViewRefreshRef = useRefreshRef();
	const billingPlanViewRefreshRef = useRefreshRef();
	const salesCommissionViewRefreshRef = useRefreshRef();

	const hasPermission = useHasPermission();
	const editable = hasPermission(Permissions.Sales.CustomerOrders.MANAGE);

	const { tenantConfig } = useTenantCustomizations();
	const tasksEnabled = useTasksEnabled();

	return (
		<CustomerOrdersPageContext.Provider
			value={{
				customerOrdersRefreshRef,
				customerOrderLinesRefreshRef,
				attributeViewRefreshRef,
				tasksViewRefreshRef,
				billingPlanViewRefreshRef,
				salesCommissionViewRefreshRef,
			}}
		>
			<AavoMosaic
				viewId={"9705F52BFA8D92A6"}
				layout={{
					type: "column",
					first: {
						type: "panel",
						content: (
							<CustomerOrdersDataGrid
								onlyCustomerOrderToShowId={onlyCustomerOrderToShowId}
								customerIdFilter={customerIdFilter}
								initialStateFilter={initialStateFilter}
								selectedCustomerOrder={selectedCustomerOrder}
								setSelectedCustomerOrder={setSelectedCustomerOrder}
								editable={editable}
							/>
						),
					},
					second: {
						type: "tabs",
						items: [
							{
								type: "panel",
								title: i18n.t("customer_order_lines"),
								content:
									selectedCustomerOrder == null ? selectCustomerOrderTypography : (
										<CustomerOrderLinesDataGrid
											key={selectedCustomerOrder.customerOrderId}
											customerOrderId={selectedCustomerOrder.customerOrderId}
											parentLineId={null}
											refreshRef={customerOrderLinesRefreshRef}
											editable={editable}
										/>
									),
							},
							tasksEnabled && {
								type: "panel",
								title: i18n.t("tasks"),
								content:
									selectedCustomerOrder == null ? selectCustomerOrderTypography : (
										<SingleSourceTasksView
											key={selectedCustomerOrder.customerOrderId}
											taskSourceRef={{
												sourceType: "CUSTOMER_ORDER",
												sourceId: selectedCustomerOrder.customerOrderId,
											}}
											refreshRef={tasksViewRefreshRef}
										/>
									),
							},
							{
								type: "panel",
								title: i18n.t("packages"),
								content:
									selectedCustomerOrder == null ? selectCustomerOrderTypography : (
										<DeliveryPackagesView customerOrderId={selectedCustomerOrder.customerOrderId} />
									),
							},
							{
								type: "panel",
								title: i18n.t("deliveries"),
								content:
									selectedCustomerOrder == null ? selectCustomerOrderTypography : (
										<CustomerOrderDeliveriesView
											customerOrderId={selectedCustomerOrder.customerOrderId}
										/>
									),
							},
							{
								type: "panel",
								title: i18n.t("billing_plan"),
								content:
									selectedCustomerOrder == null ? selectCustomerOrderTypography
									: !selectedCustomerOrder.billingPlanEnabled ?
										<CenteredTypography children={i18n.t("billing_plan_not_enabled")} />
									:	<CustomerOrderBillingPlanCrudDataGrid
											key={selectedCustomerOrder.customerOrderId}
											customerOrderId={selectedCustomerOrder.customerOrderId}
											refreshRef={billingPlanViewRefreshRef}
											customerOrdersRefreshRef={customerOrdersRefreshRef}
										/>,
							},
							{
								type: "panel",
								title: i18n.t("invoices"),
								content:
									selectedCustomerOrder == null ? selectCustomerOrderTypography : (
										<CustomerOrderInvoicesDataGrid
											key={selectedCustomerOrder.customerOrderId}
											customerOrderId={selectedCustomerOrder.customerOrderId}
										/>
									),
							},
							{
								type: "panel",
								title: i18n.t("conditions"),
								content:
									selectedCustomerOrder == null ? selectCustomerOrderTypography : (
										<CustomerOrderSalesConditionDataGrid
											key={selectedCustomerOrder.customerOrderId}
											customerOrderId={selectedCustomerOrder.customerOrderId}
											onlyIncomplete={false}
											editable={editable}
										/>
									),
							},
							selectedCustomerOrder?.salesCommissionId != null &&
								hasPermission(Permissions.Sales.CustomerOrderCommissions.MANAGE) && {
									type: "panel",
									title: i18n.t("commissions"),
									content: (
										<CustomerOrderSalesCommissionView
											key={selectedCustomerOrder.salesCommissionId}
											customerOrderId={selectedCustomerOrder.customerOrderId}
											salesCommissionId={selectedCustomerOrder.salesCommissionId}
											salesCommissionViewRefreshRef={salesCommissionViewRefreshRef}
										/>
									),
								},
							selectedCustomerOrder?.customerPriceGroupPricingMethod === "PROFIT_MARGIN" && {
								type: "panel",
								title: i18n.t("profit_margins"),
								content: (
									<CustomerOrderProfitMarginsDataGrid
										key={selectedCustomerOrder.customerOrderId}
										customerOrderId={selectedCustomerOrder.customerOrderId}
										onMarginsSaved={async () => {
											await Promise.all([
												customerOrdersRefreshRef.refresh(),
												customerOrderLinesRefreshRef.refresh(),
											]);
										}}
										editable={
											editable && selectedCustomerOrder.customerOrderState !== "CANCELLED"
										}
									/>
								),
							},
							selectedCustomerOrder != null &&
								tenantConfig.erp.partCostOverridesEnabled && {
									type: "panel",
									title: i18n.t("part_cost_overrides"),
									content: (
										<PartCostOverridesDataGrid
											key={selectedCustomerOrder.customerOrderId}
											owner={{
												type: "customerOrder",
												customerOrderId: selectedCustomerOrder.customerOrderId,
											}}
											onCostsSaved={async () => {
												await Promise.all([
													customerOrdersRefreshRef.refresh(),
													customerOrderLinesRefreshRef.refresh(),
												]);
											}}
											editable={
												editable && selectedCustomerOrder.customerOrderState !== "CANCELLED"
											}
										/>
									),
								},
							tenantConfig.erp.reclamationsEnabled &&
								hasPermission(Permissions.Reclamations.READ) && {
									type: "panel",
									title: i18n.t("reclamations"),
									content:
										selectedCustomerOrder == null ? selectCustomerOrderTypography : (
											<CustomerOrderReclamationsDataGrid
												key={selectedCustomerOrder.customerOrderId}
												customerOrder={selectedCustomerOrder}
											/>
										),
								},
							selectedCustomerOrder != null &&
								selectedCustomerOrder.costEventPriceListId != null && {
									type: "panel",
									title: i18n.t("cost_events"),
									content: (
										<CostEventsOfObjectDataGrid
											key={selectedCustomerOrder.customerOrderId}
											objectRef={{
												objectType: "CUSTOMER_ORDER",
												objectId: selectedCustomerOrder.customerOrderId,
											}}
											costEventPriceListId={selectedCustomerOrder.costEventPriceListId}
										/>
									),
								},
							{
								type: "panel",
								title: i18n.t("order_changes"),
								content:
									selectedCustomerOrder == null ? selectCustomerOrderTypography : (
										<CustomerOrderChangesDataGrid
											key={selectedCustomerOrder.customerOrderId}
											objectType="CUSTOMER_ORDER"
											objectId={selectedCustomerOrder.customerOrderId}
										/>
									),
							},
							{
								type: "panel",
								title: i18n.t("attributes"),
								content:
									selectedCustomerOrder == null ? selectCustomerOrderTypography : (
										<CustomerOrderAttributesForm
											key={selectedCustomerOrder.customerOrderId}
											customerOrderId={selectedCustomerOrder.customerOrderId}
										/>
									),
							},
							{
								type: "panel",
								title: i18n.t("info"),
								content:
									selectedCustomerOrder == null ? selectCustomerOrderTypography : (
										<CustomerOrderInfoAndCommentsView
											key={selectedCustomerOrder.customerOrderId}
											customerOrderId={selectedCustomerOrder.customerOrderId}
										/>
									),
							},
						],
					},
				}}
			/>
		</CustomerOrdersPageContext.Provider>
	);
};

const DeliveryPackagesView = ({ customerOrderId }: { customerOrderId: number }) => {
	const [selectedPackage, setSelectedPackage] = useState<DeliveryPackageWithSourcesView | undefined>(undefined);
	return (
		<AavoMosaic
			viewId={"F480E81E3E653D6B"}
			layout={{
				type: "row",
				first: {
					type: "panel",
					title: i18n.t("packages"),
					content: (
						<DeliveryPackagesDataGrid
							key={customerOrderId}
							gridId={"215795481EC3817C"}
							onSelectedPackageChanged={(selectedPackages) => setSelectedPackage(selectedPackages[0])}
							deliveryPackageSource={{
								type: "customerOrder",
								customerOrderId: customerOrderId,
							}}
						/>
					),
				},
				second: {
					type: "panel",
					title: i18n.t("package_lines"),
					content:
						selectedPackage == null ?
							<CenteredTypography children={i18n.t("select_package")} />
						:	<DeliveryPackageLinesDataGrid
								key={selectedPackage.deliveryPackageId}
								deliveryPackage={selectedPackage}
								deliveryPackageSource={{
									type: "customerOrder",
									customerOrderId: customerOrderId,
								}}
							/>,
				},
			}}
		/>
	);
};

const CustomerOrderDeliveriesView = ({ customerOrderId }: { customerOrderId: number }) => {
	const [selectedDelivery, setSelectedDelivery] = useState<DeliveryView | undefined>(undefined);
	const customerOrdersRefreshRef = useContext(CustomerOrdersPageContext)?.customerOrdersRefreshRef;
	return (
		<AavoMosaic
			viewId={"F480E81E3E658D6B"}
			layout={{
				type: "row",
				first: {
					type: "panel",
					title: i18n.t("deliveries"),
					content: (
						<DeliveriesDataGridBase
							key={customerOrderId}
							fetchData={({ dataModelRequest }) =>
								DeliveriesDataGridApi.getDeliveriesConnectedToCustomerOrder({
									customerOrderId: customerOrderId,
									dataModelRequest: dataModelRequest,
								})
							}
							gridId={"F77A4E4E5856D928"}
							onSelectionChanged={(selectedDelivery) => setSelectedDelivery(selectedDelivery)}
							onDeliveryStateChanged={customerOrdersRefreshRef?.refresh}
						/>
					),
				},
				second: {
					type: "panel",
					title: i18n.t("delivery_packages"),
					content:
						selectedDelivery == null ?
							<CenteredTypography children={i18n.t("select_delivery")} />
						:	<DeliveryPackagesDataGrid
								key={selectedDelivery.deliveryId}
								gridId={"215795487EC3817C"}
								deliveryId={selectedDelivery.deliveryId}
								disablePagination
							/>,
				},
			}}
		/>
	);
};
